import React, { useState } from 'react';
import type { MouseEvent } from 'react';
import { useHistory } from 'react-router-dom';

import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';

import { ContractRegistrationRoutes } from '@forvalter/routing/Routes';

import type { IContractToRegistration } from '../../classes';
import classNames from 'classnames';
import MUIDataTable from 'mui-datatables';
import {
    getContractAnsvarligName,
    getContractAnsvarligOekonomiName,
    getStyles,
} from './helpers';
import { getTableColumns } from './tableColumns/getTableColumns';
import { getTableOptions } from './tableOptions';

type Props = {
    registrationLines: Array<IContractToRegistration>;
};

export const ContractToRegistrationTable: React.FC<Props> = ({
    registrationLines,
}) => {
    const styles = getStyles();
    const classes = useKlpStyles({}, styles);
    const history = useHistory();

    const [openPopperId, setOpenPopperId] = useState<string | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleRowClick = (
        _rowData: Array<string>,
        rowMeta: { dataIndex: number },
    ) => {
        const dokumentId = registrationLines[rowMeta.dataIndex].dokumentId;
        if (dokumentId) {
            history.push(ContractRegistrationRoutes.single(dokumentId));
        }
    };

    const handleShareDocument = (
        dokumentId: string,
        event: React.MouseEvent,
    ) => {
        event.stopPropagation();
        history.push(ContractRegistrationRoutes.shareDocument(dokumentId));
    };

    const handleTogglePopper = (id: string, event: MouseEvent<HTMLElement>) => {
        if (openPopperId === id) {
            setOpenPopperId(null);
            setAnchorEl(null);
        } else {
            setOpenPopperId(id);
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClosePopper = () => {
        setOpenPopperId(null);
        setAnchorEl(null);
    };

    // Transform data to include computed fields
    const tableData = registrationLines.map((line) => ({
        ...line,
        byggNavn: line.bygg?.navn,
        ansvarligNavn: getContractAnsvarligName(line),
        ansvarligOekonomiNavn: getContractAnsvarligOekonomiName(line),
    }));

    const tableColumns = getTableColumns({
        registrationLines,
        classes,
        history,
        handleShareDocument,
        handleClosePopper,
        handleTogglePopper,
        openPopperId,
        anchorEl,
    });

    const tableOptions = getTableOptions({
        handleRowClick,
        registrationLines,
        openPopperId,
    });

    return (
        <div
            className={classNames(classes.table, {
                [classes.tableBlurred]: openPopperId,
            })}
        >
            <MUIDataTable
                title=""
                data={tableData}
                columns={tableColumns}
                options={tableOptions}
                data-cy="ContractToRegistrationTable__Table"
            />
        </div>
    );
};
